import { watch } from 'vue';
import type { Ref } from 'vue';
import { normalizeCmsPlainText, stripHtmlToText } from '@/utils/html';

/** Payload for document title and meta description (from listing item or page detail). */
export interface PageHeadPayload {
  title?: string;
  /** Optional rich HTML title; when set, used for document title after stripping tags (unless seo_title). */
  rich_title?: string | null;
  meta?: {
    seo_title?: string | null;
    search_description?: string | null;
  };
}

const DEFAULT_TITLE = 'INDMO';
const DEFAULT_DESCRIPTION = 'Progressive Web App';

function getOrCreateDescriptionMeta(): HTMLMetaElement | null {
  const existing = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (existing) return existing;
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'description');
  document.head.appendChild(meta);
  return meta;
}

function applyHead(
  payload: PageHeadPayload | null,
  defaultTitle: string,
  defaultDescription: string
): void {
  if (payload === null) {
    document.title = defaultTitle;
    const descMeta = getOrCreateDescriptionMeta();
    if (descMeta) descMeta.setAttribute('content', defaultDescription);
    return;
  }
  const richPlain =
    payload.rich_title && String(payload.rich_title).trim()
      ? stripHtmlToText(payload.rich_title)
      : '';
  const title =
    (payload.meta?.seo_title && normalizeCmsPlainText(payload.meta.seo_title).trim()) ||
    (richPlain && richPlain.trim()) ||
    (payload.title && payload.title.trim()) ||
    defaultTitle;
  document.title = title;
  const description =
    (payload.meta?.search_description &&
      normalizeCmsPlainText(payload.meta.search_description).trim()) ||
    defaultDescription;
  const descMeta = getOrCreateDescriptionMeta();
  if (descMeta) descMeta.setAttribute('content', description);
}

/**
 * Updates document.title and meta name="description" from the current page payload.
 * Call with a ref/computed that is the page head payload (listing item, detail, or preview);
 * pass null when no page (404 or no content) to restore defaults.
 */
export function usePageHead(
  headPayload: Ref<PageHeadPayload | null>,
  options?: { defaultTitle?: string; defaultDescription?: string }
): void {
  const defaultTitle = options?.defaultTitle ?? DEFAULT_TITLE;
  const defaultDescription = options?.defaultDescription ?? DEFAULT_DESCRIPTION;

  watch(
    headPayload,
    (payload) => {
      applyHead(payload ?? null, defaultTitle, defaultDescription);
    },
    { immediate: true }
  );
}
